// One-line catalog freshness strip for the chromium screens.
//
// Surfaces upstream sync health inline so an operator notices when the
// chromium-catalog-poll cron has gone silent BEFORE they hit a stale dropdown.
// Used both on /releases/chromium (the major catalog) and inside the
// publish form so the two surfaces report the same state.
//
// `staleAfterHours` defaults to 24h — the cron runs every 6h, so 24h is
// "we've missed at least three ticks" and warrants attention.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@veil/ui/components/badge";
import { Button } from "@veil/ui/components/button";
import { formatRelative } from "@veil/ui/lib/format";
import { cn } from "@veil/ui/lib/utils";
import { Loader2, RefreshCcw } from "lucide-react";
import { useMemo } from "react";
import { toast } from "sonner";
import type { UpstreamRelease } from "@/lib/releases/types.ts";

interface Props {
	releases: UpstreamRelease[];
	syncedAt: string | null;
	loading: boolean;
	canSync: boolean;
	staleAfterHours?: number;
	className?: string;
}

export function CatalogFreshnessStrip({
	releases,
	syncedAt,
	loading,
	canSync,
	staleAfterHours = 24,
	className,
}: Props) {
	const queryClient = useQueryClient();

	const sync = useMutation({
		mutationFn: async () => {
			const res = await fetch("/api/releases/chromium/sync-now", {
				method: "POST",
				credentials: "same-origin",
			});
			const data = await res.json();
			if (!res.ok) {
				throw new Error(data?.error?.message ?? `sync ${res.status}`);
			}
			return data;
		},
		onSuccess: () => {
			toast.success("Catalog synced");
			queryClient.invalidateQueries({ queryKey: ["chromium-upstream"] });
		},
		onError: (err) =>
			toast.error(err instanceof Error ? err.message : "Sync failed"),
	});

	const stats = useMemo(() => {
		const majors = new Set<number>();
		let withDeb = 0;
		for (const r of releases) {
			if (r.hasX64Deb) {
				majors.add(r.browserMajor);
				withDeb += 1;
			}
		}
		return { versions: withDeb, majors: majors.size };
	}, [releases]);

	const ageMs = useMemo(() => {
		if (!syncedAt) return null;
		const t = new Date(syncedAt).getTime();
		if (Number.isNaN(t)) return null;
		return Date.now() - t;
	}, [syncedAt]);

	const stale = ageMs !== null && ageMs > staleAfterHours * 60 * 60 * 1000;
	const empty = !loading && releases.length === 0;

	return (
		<div
			className={cn(
				"flex flex-wrap items-center justify-between gap-3 rounded-md border border-border/60 bg-muted/20 px-4 py-2.5 text-sm",
				className,
			)}
		>
			<div className="flex flex-wrap items-center gap-3">
				<span className="text-muted-foreground">Upstream catalog</span>
				{loading ? (
					<span className="text-xs text-muted-foreground">syncing…</span>
				) : empty ? (
					<Badge variant="warning">never synced</Badge>
				) : (
					<>
						<Badge variant={stale ? "warning" : "secondary"}>
							{syncedAt ? formatRelative(syncedAt) : "unknown"}
						</Badge>
						<span className="text-xs text-muted-foreground tabular-nums">
							{stats.versions} version{stats.versions === 1 ? "" : "s"} ·{" "}
							{stats.majors} major{stats.majors === 1 ? "" : "s"}
						</span>
						{stale ? (
							<span className="text-xs text-amber-700 dark:text-amber-400">
								Cron may be stuck — check /operations.
							</span>
						) : null}
					</>
				)}
			</div>
			{canSync ? (
				<Button
					type="button"
					size="sm"
					variant="ghost"
					onClick={() => sync.mutate()}
					disabled={sync.isPending}
				>
					{sync.isPending ? (
						<Loader2 className="mr-1 size-3.5 animate-spin" />
					) : (
						<RefreshCcw className="mr-1 size-3.5" />
					)}
					Sync now
				</Button>
			) : null}
		</div>
	);
}
